import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useLocation } from "react-router-dom";
import type { Brand } from "../../../shared/types";
import { useBrands } from "../hooks/useBrand";
import {
  isGlobalOperatorPath,
  normalizeBrandId,
  resolveBrandContextId,
} from "../lib/brand-navigation";

const STORAGE_KEY = "helloconvo:activeBrandId";

const EMPTY_BRANDS: Brand[] = [];

function getStorage(): Storage | null {
  if (typeof window !== "undefined" && window.localStorage) {
    return window.localStorage;
  }
  if (typeof globalThis !== "undefined" && "localStorage" in globalThis) {
    return globalThis.localStorage ?? null;
  }
  return null;
}

function readStoredId(): string | null {
  try {
    return getStorage()?.getItem(STORAGE_KEY) ?? null;
  } catch {
    return null;
  }
}

function writeStoredId(id: string) {
  try {
    getStorage()?.setItem(STORAGE_KEY, id);
  } catch {
    /* ignore quota / private mode */
  }
}

function clearStoredId() {
  try {
    getStorage()?.removeItem(STORAGE_KEY);
  } catch {
    /* ignore quota / private mode */
  }
}

export type ActiveBrandContextValue = {
  brands: Brand[];
  activeBrand: Brand | undefined;
  activeBrandId: string | undefined;
  setActiveBrandId: (id: string) => void;
  hasMultipleBrands: boolean;
  isLoading: boolean;
  isError: boolean;
  isLoadingError: boolean;
  refetch: () => void;
};

const ActiveBrandContext = createContext<ActiveBrandContextValue | null>(null);

/**
 * Single source of truth for active brand across Sidebar, pages, and mobile bar.
 * Must wrap any tree that calls `useActiveBrand()`.
 */
export function ActiveBrandProvider({ children }: { children: ReactNode }) {
  const brandsQuery = useBrands();
  const { data: brandsData, isLoading, isError, refetch } = brandsQuery;
  /** Initial fetch failure: TanStack v5 uses `isError`; some tests stub legacy `isLoadingError`. */
  const legacyLoadingError = Boolean(
    (brandsQuery as { isLoadingError?: boolean }).isLoadingError,
  );
  const brands: Brand[] = brandsData?.data ?? EMPTY_BRANDS;

  const [activeBrandId, setActiveBrandIdState] = useState<string | null>(null);

  // Route-derived brand id wins over persisted state so brand-scoped pages
  // (`/brand/:brandId/...`, `/agent/:brandId`) and operator pages with
  // explicit `?brandId=` never disagree with the shell.
  // The provider lives in a pathless layout route, so `useParams()` would
  // return `{}` here — read the pathname instead.
  const location = useLocation();
  const requestedBrandId = normalizeBrandId(
    resolveBrandContextId(location.pathname, location.search),
    brands,
  );
  const operatorRoute = isGlobalOperatorPath(location.pathname);

  useEffect(() => {
    if (!brandsData) {
      return;
    }
    const list = brandsData?.data;
    if (!list?.length) {
      setActiveBrandIdState(null);
      clearStoredId();
      return;
    }

    setActiveBrandIdState((prev) => {
      if (requestedBrandId) {
        // URL is canonical on a brand-scoped route. If the route brand is in
        // the list, follow it. If not (unknown id, stale bookmark, foreign
        // tenant), don't silently substitute a different brand into the
        // shell — clear selection so the page can render its unknown-brand
        // state instead of the URL and shell disagreeing.
        if (list.some((b) => b.id === requestedBrandId)) {
          return requestedBrandId;
        }
        return null;
      }
      if (prev && list.some((b) => b.id === prev)) {
        return prev;
      }
      const stored = readStoredId();
      if (stored && list.some((b) => b.id === stored)) {
        return stored;
      }
      if (stored) {
        clearStoredId();
      }
      if (operatorRoute) {
        return null;
      }
      return list[0]!.id;
    });
  }, [brandsData, requestedBrandId, operatorRoute]);

  const setActiveBrandId = useCallback(
    (id: string) => {
      const list = brandsData?.data;
      if (!list?.some((b) => b.id === id)) {
        return;
      }
      // On any brand-scoped route the URL is canonical — block setter drift
      // even when the route brand is unknown, so an invalid URL never gets
      // papered over by silently switching the shell to a different brand.
      if (requestedBrandId && id !== requestedBrandId) {
        return;
      }
      setActiveBrandIdState(id);
    },
    [brandsData, requestedBrandId],
  );

  const activeBrand = useMemo((): Brand | undefined => {
    const list = brandsData?.data;
    if (!list?.length) return undefined;
    // Resolve directly from the route id when present and known — avoids a
    // one-tick flash of "no brand" between the route changing and the sync
    // effect updating internal state.
    if (requestedBrandId) {
      return list.find((b) => b.id === requestedBrandId);
    }
    if (activeBrandId) {
      const found = list.find((b) => b.id === activeBrandId);
      if (found) return found;
    }
    if (operatorRoute) {
      return undefined;
    }
    return list[0];
  }, [brandsData, activeBrandId, requestedBrandId, operatorRoute]);

  useEffect(() => {
    const list = brandsData?.data;
    if (!list) return;
    if (activeBrand?.id && list.some((b) => b.id === activeBrand.id)) {
      writeStoredId(activeBrand.id);
      return;
    }
    if (list.length === 0) {
      clearStoredId();
    }
  }, [activeBrand, brandsData]);

  const value = useMemo((): ActiveBrandContextValue => {
    const hasMultipleBrands = brands.length > 1;
    const isLoadingError = !brandsData && (isError || legacyLoadingError);
    return {
      brands,
      activeBrand,
      activeBrandId: activeBrand?.id,
      setActiveBrandId,
      hasMultipleBrands,
      isLoading,
      isError,
      isLoadingError,
      refetch,
    };
  }, [
    brands,
    brandsData,
    activeBrand,
    setActiveBrandId,
    isLoading,
    isError,
    legacyLoadingError,
    refetch,
  ]);

  return (
    <ActiveBrandContext.Provider value={value}>{children}</ActiveBrandContext.Provider>
  );
}

/**
 * Persisted “active” brand for multi-tenant UI. Replaces the old `brands[0]` assumption.
 * Selection is stored in localStorage and validated against the current `GET /api/brands` list.
 */
export function useActiveBrand(): ActiveBrandContextValue {
  const ctx = useContext(ActiveBrandContext);
  if (!ctx) {
    throw new Error("useActiveBrand must be used within ActiveBrandProvider");
  }
  return ctx;
}
